// ─────────────────────────────────────────────────────────────────────────────────────────────
// COPIE VERSIONNÉE — DONNÉE RÉGLEMENTAIRE : NE RIEN CORRIGER ICI SANS LA SOURCE PRIMAIRE.
// Origine   : dépôt VLT Devis, public/outils/metiers/cvc/zones-climatiques.mjs
//             (C:\Users\wecha\vlt-devis-saas\public\outils\metiers\cvc\zones-climatiques.mjs)
// Version   : commit 53f27dce35157919e1bdb92bf9dcd6c1e135e83c du 18/09/2026, fichier du 18/09/2026 17:35
// Empreinte : SHA-256 du source 7A397B6273A7DC72DD62C1A4F5EAD537630CDD9CB5DF9B07F73230DA699765E3
// Copie     : 07/10/2026, portage TypeScript TEL QUEL (annotations de type seulement, table inchangée).
// Tests     : tests/unit/cvc-moteur.test.ts (test 11 de l'origine + contrôle Essonne 91 → H1a, −9,5 °C sous 400 m).
// ─────────────────────────────────────────────────────────────────────────────────────────────
// Assistant Climatisation CVC — zones climatiques et température extérieure de base.
//
// DONNÉE PUBLIQUE ET RÉGLEMENTAIRE, reprise telle quelle — rien n'est estimé ici.
// Source primaire : arrêté du 31 mars 2021 relatif aux méthodes et procédures applicables au DPE,
// annexe 1 « Méthode de calcul 3CL-DPE 2021 », §18.1 « Zones climatiques » (pages 120-121) :
//   https://rt-re-batiment.developpement-durable.gouv.fr/IMG/pdf/consolide_annexe_1_arrete_du_31_03_2021_relatif_aux_methodes_et_procedures_applicables.pdf
// Table extraite du PDF officiel puis recoupée avec https://www.methode3cl.com/18-Annexes/18.1-Zones-climatiques
// (78 des 96 départements y figurent, 100 % concordants) et avec la constante Tbase du moteur
// 3CL open source Open3CL. Consulté le 18/09/2026. 96 départements, aucun laissé en suspens.
//
// DEUX PIÈGES ÉCARTÉS, à ne pas réintroduire :
//   • Ce zonage n'est PAS celui de la RT2012 / RE2020, qui découpe plusieurs départements par
//     commune (05, 26, 38, 64, 66, 83…). Les deux cartes ne sont pas interchangeables.
//   • Dans la 3CL-DPE, l'altitude ne change PAS la zone : elle choisit seulement la tranche
//     d'altitude qui donne theta_e. La règle « au-delà de 800 m un H2 devient H1 », répandue sur
//     le web, ne figure pas dans l'annexe — l'appliquer en plus ferait une double correction.

export type ZoneClimatique = 'H1a' | 'H1b' | 'H1c' | 'H2a' | 'H2b' | 'H2c' | 'H2d' | 'H3';

/** Département (2 caractères, « 2A » et « 2B » pour la Corse) → zone climatique 3CL-DPE 2021. */
export const ZONES_DEPARTEMENT: Readonly<Record<string, ZoneClimatique>> = Object.freeze({
  '01': 'H1c', '02': 'H1a', '03': 'H1c', '04': 'H2d', '05': 'H1c', '06': 'H3', '07': 'H2d',
  '08': 'H1b', '09': 'H2c', 10: 'H1b', 11: 'H3', 12: 'H2c', 13: 'H3', 14: 'H1a', 15: 'H1c',
  16: 'H2b', 17: 'H2b', 18: 'H2b', 19: 'H1c', '2A': 'H3', '2B': 'H3', 21: 'H1c', 22: 'H2a',
  23: 'H1c', 24: 'H2c', 25: 'H1c', 26: 'H2d', 27: 'H1a', 28: 'H1a', 29: 'H2a', 30: 'H3',
  31: 'H2c', 32: 'H2c', 33: 'H2c', 34: 'H3', 35: 'H2a', 36: 'H2b', 37: 'H2b', 38: 'H1c',
  39: 'H1c', 40: 'H2c', 41: 'H2b', 42: 'H1c', 43: 'H1c', 44: 'H2b', 45: 'H1b', 46: 'H2c',
  47: 'H2c', 48: 'H2d', 49: 'H2b', 50: 'H2a', 51: 'H1b', 52: 'H1b', 53: 'H2b', 54: 'H1b',
  55: 'H1b', 56: 'H2a', 57: 'H1b', 58: 'H1b', 59: 'H1a', 60: 'H1a', 61: 'H1a', 62: 'H1a',
  63: 'H1c', 64: 'H2c', 65: 'H2c', 66: 'H3', 67: 'H1b', 68: 'H1b', 69: 'H1c', 70: 'H1b',
  71: 'H1c', 72: 'H2b', 73: 'H1c', 74: 'H1c', 75: 'H1a', 76: 'H1a', 77: 'H1a', 78: 'H1a',
  79: 'H2b', 80: 'H1a', 81: 'H2c', 82: 'H2c', 83: 'H3', 84: 'H2d', 85: 'H2b', 86: 'H2b',
  87: 'H1c', 88: 'H1b', 89: 'H1b', 90: 'H1b', 91: 'H1a', 92: 'H1a', 93: 'H1a', 94: 'H1a',
  95: 'H1a',
});

/** Température extérieure de base (°C) par groupe de zone et tranche d'altitude. §18.1. */
export const THETA_E: Readonly<Record<'H1' | 'H2' | 'H3', readonly [number, number, number]>> = Object.freeze({
  H1: [-9.5, -11.5, -13.5],
  H2: [-6.5, -8.5, -10.5],
  H3: [-3.5, -5.5, -7.5],
});

export const TRANCHES_ALTITUDE = Object.freeze([
  'altitude inférieure à 400 m',
  'altitude de 400 à 800 m',
  'altitude supérieure ou égale à 800 m',
] as const);

export const SOURCE_ZONES = 'Arrêté du 31 mars 2021 (méthode 3CL-DPE 2021), annexe 1 §18.1 — zones climatiques et températures extérieures de base par département et tranche d’altitude.';

/** Normalise une saisie de département (« 6 », « 06 », « 2a ») et renvoie sa zone, ou null. */
export function zoneDuDepartement(dep: unknown): ZoneClimatique | null {
  if (dep == null) return null;
  let d = String(dep).trim().toUpperCase().replace(/\s/g, '');
  if (/^[12][AB]$/.test(d)) d = '2' + d.slice(1);
  else if (/^\d$/.test(d)) d = '0' + d;
  else if (!/^\d{2}$/.test(d)) return null;
  return ZONES_DEPARTEMENT[d] || null;
}

/** Liste pour un menu déroulant : 96 entrées triées, zone rappelée. */
export const LISTE_DEPARTEMENTS: readonly { id: string; nom: string }[] = Object.freeze(
  Object.keys(ZONES_DEPARTEMENT)
    .map(String)
    .sort((a, b) => a.localeCompare(b, 'fr'))
    .map((d) => ({ id: d, nom: d + ' — zone ' + ZONES_DEPARTEMENT[d] })),
);
